/**
 * Awaiting Next Turn Phase — recovery burst processing, settlement waiting,
 * solo turn cycling (with dummy respawn), multiplayer turn advance.
 *
 * Extracted from phases.ts for readability.
 */

import { Time } from 'bonkjs';
import type { InputProvider } from '../input';
import { localInput } from '../input';
import type { GameState } from '../types';
import type { GameEffect } from './effects';
import { transitionPhase } from './effects';
import { updatePostFireMovement, applyRecoveryAirControl } from './movement';
import { advanceTurn } from './turns';
import { getGroundY, getGroundYBelow } from '../terrain/grid';
import { allSmoothingComplete } from '../terrain/sand-physics';
import { isSoloMode, modeUsesLottery } from './mode';
import { enterLottery } from './phases';
import { PHYSICS, TURN_FLOW, KNOCKBACK, TURN_INDICATOR, SURVIVAL } from '../constants';
import { msToFrames } from './input-edge';
import { mirrorSettle, mirrorAirborne } from '../physics/transitions';
import { closeTrickSession } from './trick-score';
import { getAllTankIds, lookupTank } from '../tanks';
import { resetPlayerForRound } from './player-defaults';

/** Check if all alive tanks have settled (low velocity, no active grapple, sand settled). */
function allTanksSettled(state: GameState): boolean {
  // Block turn advance while grapple is active
  if (state.grapple.active) return false;

  // Block turn advance while crater smoothing is in progress
  if (!allSmoothingComplete(state)) return false;

  for (const p of state.players) {
    if (!p.alive) continue;
    if (Math.abs(p.vx) > KNOCKBACK.TUMBLE_CANCEL_VX) return false;
    if (Math.abs(p.vy) > KNOCKBACK.TUMBLE_CANCEL_VY) return false;
  }
  return true;
}

export function handleAwaitingPhase(state: GameState, input: InputProvider, fx: GameEffect[], recoveryInput?: InputProvider): void {
  // Close trick session on first frame of awaiting (no-op if already closed)
  closeTrickSession(state, fx);

  // Target Blast: timer ticks during awaiting
  if (state.modeState.mode === 'target_blast' && !state.modeState.runComplete) {
    state.modeState.elapsedTime += Time.fixedDeltaTime;
  }

  // Skip timer while frozen (hitstop)
  if (state.frameCount < state.freezeUntil) return;

  // Block turn advance during void retaliation
  if (state.voidRetaliationActive) return;

  // Post-fire air control for the active player
  updatePostFireMovement(state, input);

  // Recovery air control for non-current tumbling players (sub-DI hits)
  for (let i = 0; i < state.players.length; i++) {
    if (i === state.currentPlayer) continue;
    const p = state.players[i];
    if (!p.alive || !p.tumble) continue;

    // Input source: local if this is my player, recoveryInput if opponent's
    const playerInput = (state.multiplayer?.enabled && i !== state.multiplayer.mySlot)
      ? (recoveryInput ?? input)
      : localInput;

    applyRecoveryAirControl(p, playerInput);
  }

  // Camera follows active player while they're airborne
  const ap = state.players[state.currentPlayer];
  if (ap.alive && !ap.tumble) {
    const apFeetY = ap.y + PHYSICS.ECB_RADIUS;
    const apGroundY = getGroundYBelow(ap.x, ap.y);
    if (apFeetY < apGroundY - PHYSICS.GROUND_SNAP_TOLERANCE) {
      fx.push({ type: 'camera_follow', x: ap.x, y: ap.y });
    }
  }

  fx.push({ type: 'render_projectiles', projectiles: [], wind: 0 }); // clear projectile graphics

  // Settling delay (turnTimer > 0), then immediate transition
  if (state.turnTimer > 0) {
    state.turnTimer -= Time.fixedDeltaTime;
    if (state.turnTimer <= 0) {
      // Before transitioning, check all tanks are settled
      if (!allTanksSettled(state)) {
        state.turnTimer = 0.05; // re-check next frame
        return; // was break
      }

      // Survival: respawn enemy if dead before normal turn advance
      if (state.modeState.mode === 'survival' && state.players.length >= 2) {
        const enemy = state.players[1];
        if (!enemy.alive) {
          // Pick new random tank (different from player's)
          // Math.random() is safe here — survival is solo-only, no multiplayer sync needed.
          // If survival ever becomes co-op, these must switch to seeded random().
          const tanks = getAllTankIds();
          const humanTankId = state.players[0].tankId;
          let newTankId = tanks[Math.floor(Math.random() * tanks.length)];
          while (newTankId === humanTankId && tanks.length > 1) {
            newTankId = tanks[Math.floor(Math.random() * tanks.length)];
          }
          // Scale difficulty based on kill count
          const ms = state.modeState;
          const diffIndex = Math.min(ms.survivalKills, SURVIVAL.DIFFICULTY_TABLE.length - 1);
          const newDifficulty = SURVIVAL.DIFFICULTY_TABLE[diffIndex];
          // Set identity before reset (resetPlayerForRound preserves tankId, derives color)
          enemy.tankId = newTankId;
          enemy.isAI = true;
          enemy.aiDifficulty = newDifficulty;
          // Pick a random spawn position away from the player
          const stageW = state.stageRight - state.stageLeft;
          const humanX = state.players[0].x;
          const candidates = SURVIVAL.SPAWN_POSITIONS
            .map(frac => state.stageLeft + stageW * frac)
            .filter(x => Math.abs(x - humanX) >= SURVIVAL.SPAWN_MIN_DISTANCE);
          // Fallback: if all too close, use the farthest one
          const spawnX = candidates.length > 0
            ? candidates[Math.floor(Math.random() * candidates.length)]
            : SURVIVAL.SPAWN_POSITIONS
              .map(frac => state.stageLeft + stageW * frac)
              .reduce((best, x) => Math.abs(x - humanX) > Math.abs(best - humanX) ? x : best);
          const spawnY = getGroundY(spawnX) - PHYSICS.ECB_RADIUS;
          resetPlayerForRound(enemy, spawnX, spawnY);
          enemy.name = 'ENEMY';
          // Rebuild AI controllers for new tank/difficulty
          fx.push({ type: 'rebuild_ai' });
        }
        // Fall through to normal turn advance below
      }

      if (isSoloMode(state.modeState.mode) && state.modeState.mode !== 'survival') {
        // Solo modes (training, target_blast, etc.): skip turn advance — same player keeps firing
        state.currentPlayer = 0; // always P1
        const soloP = state.players[0];
        soloP.power = 0;
        soloP.charging = false;
        soloP.chargeDir = 1;
        soloP.chargeGraceTimer = 0;
        soloP.chargeGracePower = 0;
        soloP.moveDir = 0;
        soloP.prevMoveDir = 0;
        soloP.walkCatchupTimer = 0;
        soloP.walkDeltaX = 0;
        soloP.aimHeldFrames = 0;
        soloP.fireHeld = false;
        soloP.specialUsed = false;
        soloP.specialHeld = false;
        soloP.launchArmor = 0;
        soloP.burstFlying = false;
        soloP.burstTrailCooldown = 0;
        mirrorAirborne(soloP);

        // Respawn dummy if it died (training / tutorial mode)
        if ((state.modeState.mode === 'training' || state.modeState.mode === 'tutorial') && state.players.length >= 2) {
          const dummy = state.players[1];
          if (!dummy.alive) {
            dummy.alive = true;
            dummy.pendingDeath = false;
            // Respawn at original position
            const dummyX = state.modeState.dummyX;
            dummy.x = dummyX;
            dummy.y = getGroundY(dummyX) - PHYSICS.ECB_RADIUS;
            dummy.vx = 0;
            dummy.vy = 0;
            dummy.tumble = false;
            dummy.hitstunFrames = 0;
            dummy.launchArmor = 0;
            dummy.burstFlying = false;
            dummy.settled = true;
            dummy.settledX = dummy.x;
            dummy.settledY = dummy.y;
            mirrorSettle(dummy);
            // Keep accumulated damage visible
          }
        }

        state.burstFromSpecial = false;
        transitionPhase(state, 'aiming', fx);
        state.turnCountdown = 0; // no timer
        state.freezeUntil = state.frameCount + msToFrames(TURN_FLOW.PHASE_CHANGE_FREEZE_MS);
      } else {
        // Settled — immediate transition (no dialog pause)
        advanceTurn(state, fx);
        fx.push({ type: 'turn_boundary_checkpoint' });
        const newPlayer = state.players[state.currentPlayer];

        // World-space turn indicator burst on new player
        fx.push({ type: 'particles', method: 'spawnTurnIndicator', args: [newPlayer.x, newPlayer.y, newPlayer.color] });

        // Screen effects (Sakurai: flash + shake for notification)
        fx.push({ type: 'screen_flash', color: newPlayer.color, alpha: TURN_INDICATOR.FLASH_ALPHA });
        fx.push({ type: 'screen_shake', intensity: TURN_INDICATOR.SHAKE_INTENSITY, decay: TURN_INDICATOR.SHAKE_DECAY });

        // Tank flash (Sakurai: character flash = state change)
        fx.push({
          type: 'tank_flash', playerIndex: state.currentPlayer,
          bright: TURN_INDICATOR.TANK_FLASH_BRIGHT, dark: TURN_INDICATOR.TANK_FLASH_DARK,
          duration: TURN_INDICATOR.TANK_FLASH_DURATION
        });

        // Go directly to aiming (or lottery in cosmic_lottery mode)
        state.burstFromSpecial = false;
        if (modeUsesLottery(state.modeState.mode)) {
          enterLottery(state, fx);
        } else {
          transitionPhase(state, 'aiming', fx);
          state.turnCountdown = TURN_FLOW.TURN_DURATION;
        }
        state.freezeUntil = state.frameCount + msToFrames(TURN_FLOW.PHASE_CHANGE_FREEZE_MS);
      }
    }
  }
}
